/**
 * Centralized logging utility for the tracking service
 */
export class Logger {
  private static instance: Logger | null = null;
  private debugMode: boolean = false;
  private subscribers: Set<(debugMode: boolean) => void> = new Set();

  private constructor() {}

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  setDebugMode(enabled: boolean): void {
    if (this.debugMode !== enabled) {
      this.debugMode = enabled;
      // Notify all subscribers
      this.subscribers.forEach(subscriber => subscriber(enabled));
    }
  }

  getDebugMode(): boolean {
    return this.debugMode;
  }

  subscribe(callback: (debugMode: boolean) => void): () => void {
    this.subscribers.add(callback);
    // Immediately notify of current state
    callback(this.debugMode);
    // Return unsubscribe function
    return () => this.subscribers.delete(callback);
  }

  log(message: string, ...args: any[]): void {
    if (this.debugMode) {
      console.log(message, ...args);
    }
  }

  warn(message: string, ...args: any[]): void {
    if (this.debugMode) {
      console.warn(message, ...args);
    }
  }

  error(message: string, ...args: any[]): void {
    if (this.debugMode) {
      console.error(message, ...args);
    }
  }

  debug(message: string, ...args: any[]): void {
    if (this.debugMode) {
      console.debug(message, ...args);
    }
  }

  // Helper method to create prefixed loggers for different components
  createComponentLogger(componentName: string) {
    return {
      log: (message: string, ...args: any[]) => this.log(`[${componentName}] ${message}`, ...args),
      warn: (message: string, ...args: any[]) => this.warn(`[${componentName}] ${message}`, ...args),
      error: (message: string, ...args: any[]) => this.error(`[${componentName}] ${message}`, ...args),
      debug: (message: string, ...args: any[]) => this.debug(`[${componentName}] ${message}`, ...args)
    };
  }
} 